import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  Alert,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  TextField,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid,
  Snackbar,
  Stepper,
  Step,
  StepLabel,
  InputAdornment,
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Link as LinkIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { adminApi, Notification, NotificationType, NotificationStatus, Organization } from '../services/api';

const DEFAULT_EXPIRY = () => new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 16);

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [organizationsLoading, setOrganizationsLoading] = useState(true);
  const [organizationsError, setOrganizationsError] = useState<string | null>(null);

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [detailNotification, setDetailNotification] = useState<Notification | null>(null);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [activeStep, setActiveStep] = useState(0);
  const [organizationSearch, setOrganizationSearch] = useState('');

  // Form states
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    type: 'info' as NotificationType,
    link: '',
    linkText: '',
    isModal: false,
    expiresAt: DEFAULT_EXPIRY(),
    status: 'active' as NotificationStatus,
    sendToAllOrganizations: true,
    organizationIds: [] as string[],
  });

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    fetchNotifications();
    fetchOrganizations();
  }, []);

  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await adminApi.listNotifications();
      if (response.success) {
        setNotifications(response.data.notifications);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      setOrganizationsLoading(true);
      setOrganizationsError(null);
      const response = await adminApi.listOrganizations();
      if (response.success) {
        setOrganizations(response.data.organizations || []);
      }
    } catch (err: any) {
      setOrganizationsError(err.response?.data?.message || err.message || 'Failed to load organizations');
    } finally {
      setOrganizationsLoading(false);
    }
  };

  const resetDialogState = () => {
    setActiveStep(0);
    setOrganizationSearch('');
    setFormError(null);
  };

  const handleCloseDialog = () => {
    if (formLoading) return;
    resetDialogState();
    setDialogOpen(false);
  };

  const handleOpenCreate = () => {
    setSelectedNotification(null);
    setFormData({
      title: '',
      message: '',
      type: 'info',
      link: '',
      linkText: '',
      isModal: false,
      expiresAt: DEFAULT_EXPIRY(),
      status: 'active',
      sendToAllOrganizations: true,
      organizationIds: [],
    });
    resetDialogState();
    setDialogOpen(true);
  };

  const handleOpenEdit = (notification: Notification) => {
    const notificationOrganizations = notification.organizationIds || [];
    setSelectedNotification(notification);
    setFormData({
      title: notification.title || '',
      message: notification.message,
      type: notification.type,
      link: notification.link || '',
      linkText: notification.linkText || '',
      isModal: notification.isModal,
      expiresAt: new Date(notification.expiresAt).toISOString().slice(0, 16),
      status: notification.status,
      sendToAllOrganizations: notificationOrganizations.length === 0,
      organizationIds: notificationOrganizations,
    });
    resetDialogState();
    setDialogOpen(true);
  };

  const handleOpenDelete = (notification: Notification) => {
    setSelectedNotification(notification);
    setDeleteDialogOpen(true);
  };

  const handleNextStep = () => {
    if (!formData.message || !formData.expiresAt) {
      setFormError('Message and Expiration Date are required');
      return;
    }
    setFormError(null);
    setActiveStep(1);
  };

  const handleBackStep = () => {
    setFormError(null);
    setActiveStep(0);
  };

  const handleFormSubmit = async () => {
    if (!formData.message || !formData.expiresAt) {
      setFormError('Message and Expiration Date are required');
      return;
    }

    if (!formData.sendToAllOrganizations && formData.organizationIds.length === 0) {
      setFormError('Select at least one organization or enable "Send to all organizations"');
      return;
    }

    try {
      setFormLoading(true);
      setFormError(null);

      // Convert datetime-local to ISO string
      const expiresAtISO = new Date(formData.expiresAt).toISOString();
      const normalizedOrganizationIds = formData.sendToAllOrganizations
        ? []
        : Array.from(new Set(formData.organizationIds));

      if (selectedNotification) {
        await adminApi.updateNotification(selectedNotification.id, {
          title: formData.title,
          message: formData.message,
          type: formData.type,
          link: formData.link,
          linkText: formData.linkText,
          isModal: formData.isModal,
          expiresAt: expiresAtISO,
          status: formData.status,
          organizationIds: normalizedOrganizationIds,
        });
        showSnackbar('Notification updated successfully', 'success');
      } else {
        await adminApi.createNotification({
          title: formData.title,
          message: formData.message,
          type: formData.type,
          link: formData.link,
          linkText: formData.linkText,
          isModal: formData.isModal,
          expiresAt: expiresAtISO,
          organizationIds: normalizedOrganizationIds,
        });
        showSnackbar('Notification created successfully', 'success');
      }

      setDialogOpen(false);
      resetDialogState();
      fetchNotifications();
    } catch (err: any) {
      setFormError(err.response?.data?.message || err.message || 'Action failed');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedNotification) return;

    try {
      setFormLoading(true);
      await adminApi.deleteNotification(selectedNotification.id);
      showSnackbar('Notification deleted successfully', 'success');
      setDeleteDialogOpen(false);
      fetchNotifications();
    } catch (err: any) {
      showSnackbar(err.response?.data?.message || err.message || 'Delete failed', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const getTypeColor = (type: NotificationType) => {
    switch (type) {
      case 'info': return 'primary';
      case 'success': return 'success';
      case 'warning': return 'warning';
      case 'feature': return 'secondary';
      default: return 'default';
    }
  };

  const isOrgSpecific = (notification: Notification): boolean =>
    !!(notification.organizationIds && notification.organizationIds.length > 0);

  const filteredOrganizations = useMemo(() => {
    const query = organizationSearch.trim().toLowerCase();
    if (!query) return organizations;

    return organizations.filter((org) => {
      const name = (org.name || '').toLowerCase();
      const displayName = (org.displayName || '').toLowerCase();
      const domain = (org.domain || '').toLowerCase();
      return name.includes(query) || displayName.includes(query) || domain.includes(query);
    });
  }, [organizations, organizationSearch]);

  const organizationSelectionModel: GridRowSelectionModel = formData.organizationIds;

  const columns: GridColDef[] = [
    {
      field: 'type',
      headerName: 'Type',
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={params.value}
            size="small"
            color={getTypeColor(params.value as NotificationType)}
            variant="outlined"
          />
        </Box>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Chip
            label={params.value}
            size="small"
            color={params.value === 'active' ? 'success' : 'default'}
          />
        </Box>
      ),
    },
    {
      field: 'organizations',
      headerName: 'Organizations',
      width: 150,
      sortable: false,
      valueGetter: (_value, row) => isOrgSpecific(row as Notification) ? 'Org specific' : 'All orgs',
      renderCell: (params: GridRenderCellParams) => {
        const specific = isOrgSpecific(params.row as Notification);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
            <Chip
              size="small"
              label={specific ? 'Org specific' : 'All orgs'}
              color={specific ? 'primary' : 'default'}
              variant="outlined"
              onClick={specific ? (e) => { e.stopPropagation(); setDetailNotification(params.row as Notification); } : undefined}
              sx={specific ? { cursor: 'pointer' } : undefined}
            />
          </Box>
        );
      },
    },
    {
      field: 'title',
      headerName: 'Title',
      flex: 1,
      minWidth: 150,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {params.value || '-'}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'message',
      headerName: 'Message',
      flex: 2,
      minWidth: 250,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Tooltip title={params.value}>
            <Typography variant="body2" noWrap sx={{ maxWidth: '100%' }}>
              {params.value}
            </Typography>
          </Tooltip>
        </Box>
      ),
    },
    {
      field: 'expiresAt',
      headerName: 'Expires',
      width: 180,
      valueGetter: (value) => new Date(value).toLocaleString(),
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <Typography variant="body2">
            {params.formattedValue}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 120,
      sortable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', height: '100%' }}>
          <Tooltip title="Edit">
            <IconButton size="small" color="primary" onClick={() => handleOpenEdit(params.row)}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton size="small" color="error" onClick={() => handleOpenDelete(params.row)}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  const organizationColumns: GridColDef[] = [
    { field: 'name', headerName: 'Organization ID', flex: 1, minWidth: 180 },
    { field: 'displayName', headerName: 'Display Name', flex: 1, minWidth: 180, valueGetter: (value) => value || '-' },
    { field: 'domain', headerName: 'Domain', flex: 1, minWidth: 180, valueGetter: (value) => value || '-' },
  ];

  if (loading && notifications.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" fontWeight={700}>
            Notifications
          </Typography>
          <Chip label={`${notifications.length} total`} color="primary" variant="outlined" />
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreate}
        >
          Create Notification
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Card>
        <DataGrid
          rows={notifications}
          columns={columns}
          autoHeight
          pageSizeOptions={[10, 25, 50]}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: {
              sortModel: [{ field: 'createdAt', sort: 'desc' }],
            },
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell:focus': { outline: 'none' },
          }}
        />
      </Card>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} fullScreen>
        <DialogTitle>
          <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
            {selectedNotification ? 'Edit Notification' : 'Create New Notification'}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%' }}>
            <Box sx={{ mb: 3 }}>
              <Stepper activeStep={activeStep}>
                <Step>
                  <StepLabel>Notification Details</StepLabel>
                </Step>
                <Step>
                  <StepLabel>Organizations</StepLabel>
                </Step>
              </Stepper>
            </Box>
          </Box>

          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}

          {activeStep === 0 && (
            <Box sx={{ maxWidth: 1100, mx: 'auto', mt: 0.5 }}>
              <Grid container spacing={3}>
                <Grid size={{ xs: 12, md: 7 }}>
                  <Box sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2, height: '100%' }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      Content
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="Title (Optional)"
                          placeholder="e.g. New Feature Alert!"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          required
                          multiline
                          rows={6}
                          label="Message"
                          placeholder="The content of the notification shown to users..."
                          value={formData.message}
                          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        />
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>

                <Grid size={{ xs: 12, md: 5 }}>
                  <Box sx={{ p: 2.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
                      Display Options
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12 }}>
                        <FormControl fullWidth required>
                          <InputLabel>Type</InputLabel>
                          <Select
                            value={formData.type}
                            label="Type"
                            onChange={(e) => setFormData({ ...formData, type: e.target.value as NotificationType })}
                          >
                            <MenuItem value="info">Info (Blue)</MenuItem>
                            <MenuItem value="success">Success (Green)</MenuItem>
                            <MenuItem value="warning">Warning (Orange)</MenuItem>
                            <MenuItem value="feature">Feature (Purple)</MenuItem>
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          required
                          label="Expires At"
                          type="datetime-local"
                          value={formData.expiresAt}
                          onChange={(e) => setFormData({ ...formData, expiresAt: e.target.value })}
                          InputLabelProps={{ shrink: true }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="Action Link (Optional)"
                          placeholder="https://..."
                          value={formData.link}
                          onChange={(e) => setFormData({ ...formData, link: e.target.value })}
                          InputProps={{
                            startAdornment: <LinkIcon fontSize="small" color="action" sx={{ mr: 1 }} />,
                          }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <TextField
                          fullWidth
                          label="Link Button Text"
                          placeholder="Learn More"
                          value={formData.linkText}
                          onChange={(e) => setFormData({ ...formData, linkText: e.target.value })}
                        />
                      </Grid>
                      <Grid size={{ xs: 12 }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.isModal}
                              onChange={(e) => setFormData({ ...formData, isModal: e.target.checked })}
                            />
                          }
                          label="Show as Modal (blocks UI until closed)"
                        />
                      </Grid>
                      {selectedNotification && (
                        <Grid size={{ xs: 12 }}>
                          <FormControl fullWidth>
                            <InputLabel>Status</InputLabel>
                            <Select
                              value={formData.status}
                              label="Status"
                              onChange={(e) => setFormData({ ...formData, status: e.target.value as NotificationStatus })}
                            >
                              <MenuItem value="active">Active</MenuItem>
                              <MenuItem value="inactive">Inactive</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}

          {activeStep === 1 && (
            <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%', mt: 0.5 }}>
              {organizationsError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {organizationsError}
                </Alert>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1.5 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.sendToAllOrganizations}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setFormData({
                          ...formData,
                          sendToAllOrganizations: checked,
                          organizationIds: checked ? [] : formData.organizationIds,
                        });
                      }}
                    />
                  }
                  label="Send to all organizations"
                />

                {!formData.sendToAllOrganizations && (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setFormData({ ...formData, organizationIds: filteredOrganizations.map((org) => org.id) })}
                      disabled={filteredOrganizations.length === 0}
                    >
                      Select all filtered
                    </Button>
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      onClick={() => setFormData({ ...formData, organizationIds: [] })}
                      disabled={formData.organizationIds.length === 0}
                    >
                      Clear all
                    </Button>
                  </Box>
                )}
              </Box>

              {formData.sendToAllOrganizations ? (
                <Alert severity="info">
                  This notification will be visible to all current and future organizations.
                </Alert>
              ) : (
                <>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="Search organizations by ID, display name, or domain..."
                    value={organizationSearch}
                    onChange={(e) => setOrganizationSearch(e.target.value)}
                    sx={{ mb: 2 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon fontSize="small" />
                        </InputAdornment>
                      ),
                    }}
                  />

                  {organizationsLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                      <CircularProgress size={28} />
                    </Box>
                  ) : (
                    <Box sx={{ height: 430 }}>
                      <DataGrid
                        rows={filteredOrganizations}
                        columns={organizationColumns}
                        checkboxSelection
                        disableRowSelectionOnClick
                        rowSelectionModel={organizationSelectionModel}
                        onRowSelectionModelChange={(newSelectionModel) => {
                          const selectedIds = (newSelectionModel as string[]).map(String);
                          setFormData({
                            ...formData,
                            sendToAllOrganizations: false,
                            organizationIds: selectedIds,
                          });
                        }}
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                          pagination: { paginationModel: { pageSize: 10 } },
                          sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
                        }}
                        sx={{
                          border: '1px solid',
                          borderColor: 'divider',
                          '& .MuiDataGrid-cell:focus': { outline: 'none' },
                        }}
                      />
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Box sx={{ maxWidth: 1100, mx: 'auto', width: '100%', display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button onClick={handleCloseDialog} disabled={formLoading}>
              Cancel
            </Button>
            {activeStep === 1 && (
              <Button onClick={handleBackStep} disabled={formLoading}>
                Back
              </Button>
            )}
            {activeStep === 0 ? (
              <Button variant="contained" onClick={handleNextStep} disabled={formLoading}>
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleFormSubmit}
                disabled={formLoading}
                startIcon={formLoading ? <CircularProgress size={20} /> : null}
              >
                {selectedNotification ? 'Update' : 'Create'}
              </Button>
            )}
          </Box>
        </DialogActions>
      </Dialog>

      {/* Org Detail Dialog */}
      {detailNotification && (() => {
        const targetedOrgs = organizations.filter(
          (org) => detailNotification.organizationIds?.includes(org.id)
        );
        return (
          <Dialog open={true} onClose={() => setDetailNotification(null)} maxWidth="sm" fullWidth>
            <DialogTitle>
              Targeted Organizations ({detailNotification.organizationIds?.length ?? 0})
            </DialogTitle>
            <DialogContent dividers>
              {targetedOrgs.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No matching organizations found.
                </Typography>
              ) : (
                <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse' }}>
                  <Box component="thead">
                    <Box component="tr">
                      <Box component="th" sx={{ textAlign: 'left', pb: 1, pr: 2, typography: 'caption', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Organization
                      </Box>
                      <Box component="th" sx={{ textAlign: 'left', pb: 1, typography: 'caption', color: 'text.secondary', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                        Domain
                      </Box>
                    </Box>
                  </Box>
                  <Box component="tbody">
                    {targetedOrgs.map((org, i) => (
                      <Box component="tr" key={org.id} sx={{ borderTop: i > 0 ? '1px solid' : 'none', borderColor: 'divider' }}>
                        <Box component="td" sx={{ py: 1, pr: 2 }}>
                          <Typography variant="body2" fontWeight={500}>
                            {org.displayName || org.name}
                          </Typography>
                          {org.displayName && org.displayName !== org.name && (
                            <Typography variant="caption" color="text.secondary">
                              {org.name}
                            </Typography>
                          )}
                        </Box>
                        <Box component="td" sx={{ py: 1 }}>
                          <Typography variant="body2" color="text.secondary">{org.domain || '-'}</Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setDetailNotification(null)}>Close</Button>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => !formLoading && setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Notification</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to permanently delete this notification?
            This action cannot be undone.
          </Typography>
          {selectedNotification && (
            <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2" fontWeight={600}>
                {selectedNotification.title || 'Untitled'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedNotification.message}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={formLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={formLoading}
            startIcon={formLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default NotificationsPage;
